import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./AppBanner.module.scss";

type Props = {
  title?: string;
  subtitle?: string;
  androidUrl: string;
  iosUrl: string;
  iconSrc: string;
  storageKey?: string;
  maxWidthToShow?: number;
};

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/.test(navigator.userAgent);
}

function isStandaloneMode() {
  if (typeof window === "undefined") return false;
  const w = window as any;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    w.navigator?.standalone === true
  );
}

export default function AppBanner({
  title,
  subtitle,
  androidUrl,
  iosUrl,
  iconSrc,
  storageKey = "airtiketa_app_banner_dismissed_v2",
  maxWidthToShow = 768,
}: Props) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  const storeUrl = useMemo(() => {
    if (isIOS()) return iosUrl;
    if (isAndroid()) return androidUrl;
    return androidUrl;
  }, [androidUrl, iosUrl]);

  const dispatchBannerEvent = () => {
    window.dispatchEvent(new Event("app-banner-changed"));
  };

  useEffect(() => {
    const compute = () => {
      const isSmallScreen = window.innerWidth <= maxWidthToShow;
      const hidden = sessionStorage.getItem(storageKey) === "1";
      const standalone = isStandaloneMode();
      setVisible(isSmallScreen && !hidden && !standalone);
    };

    compute();
    window.addEventListener("resize", compute, { passive: true });
    return () => window.removeEventListener("resize", compute);
  }, [maxWidthToShow, storageKey]);

  useEffect(() => {
    if (visible) document.body.classList.add("has-app-banner");
    else document.body.classList.remove("has-app-banner");

    dispatchBannerEvent();

    return () => {
      document.body.classList.remove("has-app-banner");
      dispatchBannerEvent();
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      id="app-banner"
      className={styles.banner}
      role="region"
      aria-label={t("app.banner.aria", "App banner")}
    >
      <button
        type="button"
        className={styles.closeBtn}
        aria-label={t("app.banner.close", "Close")}
        onClick={() => {
          sessionStorage.setItem(storageKey, "1");
          setVisible(false);
          window.dispatchEvent(new Event("app-banner-changed"));
        }}
      >
        ×
      </button>
  
      <div className={styles.iconWrap}>
        <img className={styles.icon} src={iconSrc} alt={t("app.banner.alt", "Airtiketa")} />
      </div>
  
      <div className={styles.text}>
        <div className={styles.title}>
          {title || t("app.banner.title", "Airtiketa – Book Flights")}
        </div>
        <div className={styles.subtitle}>
          {subtitle || t("app.banner.subtitle", "Search, buy & manage Holiday")}
        </div>
      </div>
  
      <a className={styles.openBtn} href={storeUrl} target="_blank" rel="noreferrer">
        {t("app.banner.open", "Open")}
      </a>
    </div>
  );  
}
